/**
 * Протокол WebSocket для совместной работы в гриде (решение Р-03).
 *
 * Модель синхронизации — НЕ CRDT и не OT, и это осознанно:
 * правка атомарна на уровне ячейки (одно поле одной строки), совместного
 * набора текста в одной ячейке двумя людьми в этой предметной области
 * не бывает. Поэтому: per-cell last-write-wins + версия строки для
 * обнаружения конфликта + широковещательная рассылка применённых изменений.
 *
 * Событие публикуется ТОЛЬКО после успешного коммита транзакции —
 * иначе клиенты увидят правку, которой в базе нет.
 */

import type { CandidateRow } from '../schemas/candidate';
import type { AppearanceSettings } from '../schemas/appearance';
import type { SecuritySettings } from '../schemas/security';
import type { ActivityContext, ActivitySelection } from '../schemas/activity';
import type { ClientObservationMessage, ServerObservationState } from '../schemas/observation';

export const WS_PATH = '/ws';

/** Версия протокола. При несовпадении клиент просят перезагрузиться. */
export const WS_PROTOCOL_VERSION = 1;

// ─────────────────────────────────────────────────────────────
// Клиент → сервер
// ─────────────────────────────────────────────────────────────

export interface ClientHello {
  type: 'client.hello';
  protocolVersion: number;
  /** Последний применённый клиентом seq — для доката после реконнекта */
  lastSeq?: number;
}

/**
 * Подписка на видимое окно строк. Клиент не получает событий по строкам,
 * которых не видит: при 100 000 строк рассылать всё всем — верный способ
 * положить браузер.
 */
export interface ClientSubscribe {
  type: 'view.subscribe';
  /** id строк в текущем окне виртуализации + строки, которые клиент правит */
  rowIds: string[];
}

export interface ClientActivity {
  type: 'activity.update';
  context?: ActivityContext;
  selection?: ActivitySelection | null;
}

export interface ClientCursor {
  type: 'cursor.move';
  rowId: string | null;
  field: string | null;
  /** Прямоугольное выделение, если выделен диапазон */
  selection?: { rowIds: string[]; fields: string[] } | null;
}

export interface ClientEditBegin {
  type: 'cell.edit.begin';
  rowId: string;
  field: string;
}

export interface ClientEditEnd {
  type: 'cell.edit.end';
  rowId: string;
  field: string;
}

export interface ClientPing {
  type: 'ping';
  t: number;
}

export type ClientMessage =
  | ClientObservationMessage
  | ClientActivity
  | ClientHello
  | ClientSubscribe
  | ClientCursor
  | ClientEditBegin
  | ClientEditEnd
  | ClientPing;

// ─────────────────────────────────────────────────────────────
// Сервер → клиент
// ─────────────────────────────────────────────────────────────

export interface PresenceUser {
  userId: string;
  name: string;
  initials: string;
  color: string;
  role: string;
  /** Где сейчас курсор этого пользователя */
  rowId: string | null;
  field: string | null;
  /** Какую ячейку он редактирует прямо сейчас */
  editing: { rowId: string; field: string } | null;
  context?: ActivityContext | null;
  selection?: ActivitySelection | null;
  lastActivityAt?: string;
}

export interface ServerWelcome {
  type: 'server.welcome';
  protocolVersion: number;
  connectionId: string;
  /** Текущий номер последовательности событий */
  seq: number;
  you: PresenceUser;
  presence: PresenceUser[];
}

export interface ServerPresence {
  type: 'presence.state';
  presence: PresenceUser[];
}

/**
 * Изменена ячейка. Прилетает всем, кроме автора — автор уже применил
 * правку оптимистично и узнаёт своё эхо по clientOpId.
 */
export interface ServerCellChanged {
  type: 'cell.changed';
  seq: number;
  rowId: string;
  field: string;
  value: unknown;
  /** Производные поля, которые сервер пересчитал: имя города при смене cityId */
  derived?: Record<string, unknown>;
  version: number;
  actorId: string;
  actorName: string;
  clientOpId: string | null;
  at: string;
}

export interface ServerRowCreated {
  type: 'row.created';
  seq: number;
  row: CandidateRow;
  actorId: string;
  actorName: string;
  clientOpId: string | null;
}

export interface ServerRowDeleted {
  type: 'row.deleted';
  seq: number;
  rowId: string;
  actorId: string;
  actorName: string;
}

export interface ServerRowRestored {
  type: 'row.restored';
  seq: number;
  row: CandidateRow;
  actorId: string;
  actorName: string;
}

/**
 * Строку перетащили.
 *
 * Везёт ПАРЫ «строка → новый номер», а не новый порядок группы: при обычном
 * перетаскивании их единицы, и остальным клиентам этого достаточно, чтобы
 * переставить строки у себя. Применение частичного набора безопасно —
 * непереставленные строки отрезка съезжают на один слот в одну сторону,
 * их взаимный порядок сохраняется.
 *
 * Если пар слишком много, вместо события уходит `server.reload_required`:
 * порог стоит в маршруте. Слать `resync` на КАЖДОЕ перетаскивание нельзя —
 * строки перекладывают очередями, и полная перезагрузка таблицы у всех
 * на каждое движение мыши это не realtime, а его отсутствие.
 */
export interface ServerRowMoved {
  type: 'row.moved';
  seq: number;
  movedId: string;
  pairs: Array<{ id: string; rowNumber: number }>;
  actorId: string;
  actorName: string;
}

/**
 * Массовая операция сворачивается в ОДНО событие.
 * Правка 500 строк не должна порождать 500 сообщений.
 */
export interface ServerBulkChanged {
  type: 'rows.bulk_changed';
  seq: number;
  rowIds: string[];
  /** Какие поля затронуты — клиент перезапрашивает только их */
  fields: string[];
  actorId: string;
  actorName: string;
  clientOpId: string | null;
}

/**
 * Скрытие и восстановление меняют состав выдачи. Адресов строк здесь нет:
 * восстановленную строку другие клиенты ещё не могли подписать.
 */
export interface ServerRowsVisibilityChanged {
  type: 'rows.visibility_changed';
  seq: number;
  clientOpId: string;
}

/** Личные правила доставляются только вкладкам владельца; null — изменение для всех. */
export interface ServerDateVisibilityChanged {
  type: 'date_visibility.changed';
  seq: number;
  userId: string | null;
}

/**
 * Слишком много изменений сразу (импорт, массовая правка на тысячи строк):
 * вместо потока событий клиенту говорят перезагрузить окно данных.
 */
export interface ServerReloadRequired {
  type: 'server.reload_required';
  seq: number;
  reason: 'import' | 'bulk_too_large' | 'protocol_mismatch' | 'resync';
  message: string;
  /**
   * Кто вызвал перезагрузку.
   *
   * Без этого поля автор действия получал собственное эхо: вставил строку,
   * применил её у себя — и следом сам себе приказывал перечитать таблицу
   * целиком. Две полные перезагрузки на одну вставку, и обе видно.
   * Остальные события протокола автора несут с самого начала.
   */
  actorId?: string | null;
}

/**
 * Изменился лист (Р-22).
 *
 * ЗНАЧЕНИЙ ЗДЕСЬ НЕТ, и это главное в устройстве события. У листов права
 * доходят до колонки: одна и та же строка приезжает рекрутёру без номера
 * карты, а администратору — с ним. Вырезает эти колонки единственное
 * место — `readRows` на сервере. Положи значение в широковещательное
 * событие, и оно уедет мимо этого заслона всем сразу.
 *
 * Поэтому здесь только адрес: какой лист, какие строки, какие колонки
 * затронуты. Клиент, у которого этот лист открыт, перечитывает его тем же
 * запросом, что и всегда, — то есть через заслон.
 *
 * Присылается всем, а не подписчикам листа: подписки на листы нет, и
 * заводить её ради события в сотню байт незачем. Отбирает клиент.
 */
export interface ServerSheetChanged {
  clientOpId?: string;
  type: 'sheet.changed';
  seq: number;
  /** Код листа: по нему клиент решает, его это или чужое */
  code: string;
  /** Что изменилось: ячейки, состав строк или устройство листа */
  kind: 'cells' | 'rows' | 'structure';
  /** Сколько строк затронуто — для подписи, не для точечного обновления */
  rows: number;
  actorId: string;
  actorName: string;
}

/** Фоновая задача завершилась — импорт, экспорт, обработка файла. */
export interface ServerJobUpdate {
  type: 'job.update';
  jobId: string;
  kind: 'import' | 'export' | 'file' | 'ocr';
  status: 'queued' | 'running' | 'done' | 'failed';
  progress: number;
  message?: string;
  resultUrl?: string;
}

/**
 * Администратор поменял общие настройки безопасности.
 *
 * Предел копирования приезжает вместе с сессией, и после перезагрузки
 * страницы он и так свежий. Это событие закрывает промежуток между
 * «администратор нажал Сохранить» и «рекрутёр обновил вкладку»: вкладка
 * с открытой таблицей у него живёт весь день.
 *
 * Доставка ненадёжна по устройству: соединение поднимает только грид, а
 * события идут через Redis, без которого приложение продолжает работать.
 * Поэтому источник истины — `/session/me` при загрузке, а это событие —
 * уточнение поверх него.
 */
export interface ServerSettingsChanged {
  type: 'settings.changed';
  /**
   * Приходит та половина, которую поменяли: за раз меняют либо предел
   * копирования, либо вид таблицы. Обе необязательны, потому что событие
   * одно на оба раздела, а слать соседний без нужды — значит перетирать
   * его у того, кто его прямо сейчас правит.
   */
  security?: SecuritySettings;
  appearance?: AppearanceSettings;
  /** Общий цвет городов без собственного цвета. */
  cityDefaultColor?: string;
  /** Перечитать оформление городов: фон, цвет текста, жирность и наследование. */
  cityColorsChanged?: boolean;
}

export interface ServerError {
  type: 'server.error';
  code: string;
  message: string;
}

export interface ServerPong {
  type: 'pong';
  t: number;
}

export type ServerMessage =
  | ServerObservationState
  | { type: 'activity.logged'; actorId: string }
  | ServerWelcome
  | ServerPresence
  | ServerCellChanged
  | ServerRowCreated
  | ServerRowDeleted
  | ServerRowRestored
  | ServerRowMoved
  | ServerBulkChanged
  | ServerRowsVisibilityChanged
  | ServerDateVisibilityChanged
  | ServerReloadRequired
  | ServerSheetChanged
  | ServerJobUpdate
  | ServerSettingsChanged
  | ServerError
  | ServerPong;

/** Канал Redis Pub/Sub, через который инстансы API обмениваются событиями. */
export const REDIS_EVENTS_CHANNEL = 'yana:events';
/** Ключ счётчика seq — общий для всех инстансов. */
export const REDIS_SEQ_KEY = 'yana:events:seq';
/** Буфер последних событий для доката после реконнекта. */
export const REDIS_EVENTS_BUFFER = 'yana:events:buffer';
export const EVENTS_BUFFER_SIZE = 2000;

/** Порог, после которого вместо потока событий шлём reload_required. */
export const BULK_EVENT_THRESHOLD = 200;

/** Как часто клиент шлёт cursor.move, мс. Чаще — лишний трафик без пользы. */
export const CURSOR_THROTTLE_MS = 150;
export const HEARTBEAT_INTERVAL_MS = 25_000;
export const RECONNECT_BASE_DELAY_MS = 500;
export const RECONNECT_MAX_DELAY_MS = 15_000;
